import {TextField} from '@mui/material'
import React, {FormEvent, useMemo, useState} from 'react'
import {useToggle} from '../../common/hooks/useToggle'
import {toDisplayDateTime} from '../../common/utils/dateUtils'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {CheckIcon} from '../../webui/icons/CheckIcon'
import {CloseIcon} from '../../webui/icons/CloseIcon'
import {EditIcon} from '../../webui/icons/EditIcon'
import {Paper} from '../../webui/Paper'
import {Typography} from '../../webui/Typography'
import {AddInternalRentalCommentVars, useAddInternalRentalComment} from './gql/useAddInternalRentalComment'
import {GQL_EditInternalRentalCommentResponseType, useEditInternalRentalCommentMutation} from '../../generated/graphql'

type CommentUser = {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
}

export type InternalComment = {
  id: string
  comment: string
  createdAt: string
  createdByUser: CommentUser
  editedAt: string | null
}

interface Props {
  rentalId: string
  comments: InternalComment[]
  refetch?: () => void
}

export const InternalRentalComments: React.FC<Props> = ({rentalId, comments, refetch}) => {
  const [newComment, setNewComment] = useState('')

  const [mutateAddInternalComment, {loading}] = useAddInternalRentalComment({
    variables: undefined,
  })

  const sortedInteralComments = useMemo(
    () => [...(comments || [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [comments],
  )

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const text = newComment.trim() // Use textarea state for submission
    if (!text) return

    const res = await mutateAddInternalComment({
      variables: {rentalId, comment: text} as AddInternalRentalCommentVars,
      // Ensure any active queries (like the rental detail) are refreshed so the new comment appears without a manual reload.
      refetchQueries: 'active',
      awaitRefetchQueries: false,
    })

    if (res.data?.addInternalRentalComment.comment) {
      setNewComment('') // Clear textarea after successful submit
    }
  }

  return (
    <Paper>
      <Box p={2}>
        <Typography variant="h4">Internal comments</Typography>

        <Box mt={2}>
          {sortedInteralComments.length === 0 ? (
            <Typography>No comments yet.</Typography>
          ) : (
            <Box display="flex" flexDirection="column" style={{gap: 8}}>
              {/* Use CSS gap via style to keep Box props unchanged */}
              {sortedInteralComments.map((internalComment) => {
                return <InternalCommentItem key={internalComment.id} internalComment={internalComment} refetch={refetch} />
              })}
            </Box>
          )}
        </Box>

        <form onSubmit={handleSubmit}>
          <Box mt={2} display="flex" style={{gap: 8, alignItems: 'flex-start'}}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={8}
              value={newComment}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNewComment(e.target.value)}
              placeholder="Add a comment"
            />
            <Button type="submit" disabled={loading || !newComment.trim()}>
              Add
            </Button>
          </Box>
        </form>
      </Box>
    </Paper>
  )
}

interface InternalCommentItemProps {
  internalComment: InternalComment
  refetch?: () => void
}

const InternalCommentItem: React.FC<InternalCommentItemProps> = ({internalComment, refetch}) => {
  const [isEditing, toggleIsEditing, setIsEditing] = useToggle(false)
  const [updatedComment, setUpdatedComment] = useState(internalComment.comment)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const resolveErrorMessage = (type: GQL_EditInternalRentalCommentResponseType): string => {
    switch (type) {
      case GQL_EditInternalRentalCommentResponseType.CommentDoesNotExist:
        return 'Comment does not exist'
      case GQL_EditInternalRentalCommentResponseType.InvalidInput:
        return 'Invalid input'
    }
    return 'Failed to edit comment'
  }

  const [mutateEditInternalComment] = useEditInternalRentalCommentMutation({
    onCompleted: (data) => {
      if (data.editInternalRentalComment.type === GQL_EditInternalRentalCommentResponseType.Success) {
        setIsEditing(false)
        refetch?.()
      } else {
        setErrorMessage(resolveErrorMessage(data.editInternalRentalComment.type))
      }
    },
  })

  const u = internalComment.createdByUser
  const who = u?.firstName || u?.lastName ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() : (u?.email ?? u?.id)

  const handleSave = async () => {
    const text = updatedComment.trim() // Use textarea state for submission
    if (!text) return
    mutateEditInternalComment({
      variables: {
        commentId: internalComment.id,
        comment: text,
      },
    })
  }

  return (
    <Paper
      key={internalComment.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 1,
      }}
    >
      <Box p={1.5}>
        <Typography variant="body2" style={{opacity: 0.8}}>
          {toDisplayDateTime(internalComment.createdAt)} — {who}
        </Typography>
        {isEditing ? (
          <div>
            <TextField fullWidth multiline minRows={1} maxRows={8} value={updatedComment} onChange={(e) => setUpdatedComment(e.target.value)} />
          </div>
        ) : (
          <pre style={{fontSize: 15}}>{internalComment.comment}</pre>
        )}
        {internalComment.editedAt && (
          <Typography variant="caption" style={{opacity: 0.8}}>
            Edited at {toDisplayDateTime(internalComment.editedAt)}
          </Typography>
        )}
      </Box>
      {isEditing ? (
        <div>
          <CheckIcon onClick={handleSave} />
          <CloseIcon onClick={toggleIsEditing} />
        </div>
      ) : (
        <EditIcon onClick={toggleIsEditing} />
      )}
      {errorMessage && (
        <Typography variant="caption" style={{color: 'red'}}>
          {errorMessage}
        </Typography>
      )}
    </Paper>
  )
}
